import { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    FormControlLabel,
    Switch,
    Button,
    Grid,
    Divider,
    InputAdornment,
    Paper
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CertificatesUploadModal from '../../components/modals/CertificatesUploadModal';

function SystemConfigSection({ config, onChange, onSave, hasChanges, localisation, deploymentType }) {
    const [certUploadModalOpen, setCertUploadModalOpen] = useState(false);
    const isCloud = deploymentType === 'cloud';

    return (
        <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                System Settings
            </Typography>

            <Grid container spacing={2}>
                {/* Server Configuration */}
                <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Server Configuration
                    </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Port"
                        type="number"
                        value={config.port || '3000'}
                        onChange={(e) => onChange('port', e.target.value)}
                        helperText="Server port (requires restart)"
                        inputProps={{ min: 1, max: 65535 }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config.blankFileLimitEnabled !== false}
                                onChange={(e) => {
                                onChange('blankFileLimitEnabled', e.target.checked);
                                if (!e.target.checked) {
                                    onChange('blankFileLimitKb', 0);
                                } else if (!config.blankFileLimitKb) {
                                    onChange('blankFileLimitKb', 10);
                                }
                            }}
                            />
                        }
                        label="Enable Blank File Limit"
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Blank File Limit"
                        type="number"
                        value={config.blankFileLimitEnabled === false ? 0 : (config.blankFileLimitKb || 10)}
                        onChange={(e) => onChange('blankFileLimitKb', parseInt(e.target.value) || 10)}
                        helperText="Files below this size are considered blank"
                        disabled={config.blankFileLimitEnabled === false}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">KB</InputAdornment>,
                        }}
                        inputProps={{ min: 1 }}
                    />
                </Grid>
                {!isCloud && (
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.useHttps || false}
                                    onChange={(e) => onChange('useHttps', e.target.checked)}
                                />
                            }
                            label="Use HTTPS"
                        />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config.auditing !== false}
                                onChange={(e) => onChange('auditing', e.target.checked)}
                            />
                        }
                        label="Enable Auditing"
                    />
                </Grid>

                {/* Throttling */}
                <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                        Request Throttling
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config.applyThrottle !== false}
                                onChange={(e) => onChange('applyThrottle', e.target.checked)}
                            />
                        }
                        label="Apply Throttle"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Delay (ms)"
                        type="number"
                        value={config.delay || 2000}
                        onChange={(e) => onChange('delay', parseInt(e.target.value) || 0)}
                        helperText="Throttle delay in milliseconds"
                        disabled={!config.applyThrottle}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                        }}
                        inputProps={{ min: 0 }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Request Limit"
                        type="number"
                        value={config.limit || 1000}
                        onChange={(e) => onChange('limit', parseInt(e.target.value) || 0)}
                        helperText="Max requests per window"
                        disabled={!config.applyThrottle}
                        inputProps={{ min: 1 }}
                    />
                </Grid>

                {/* Additional Options */}
                <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                        Additional Options
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config.distributionReport || false}
                                onChange={(e) => onChange('distributionReport', e.target.checked)}
                            />
                        }
                        label="Distribution Report"
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config.stripPdfTags === true}
                                onChange={(e) => onChange('stripPdfTags', e.target.checked)}
                            />
                        }
                        label="Strip tags from pdfs"
                    />
                </Grid>
            </Grid>

            <Button
                variant="contained"
                color="primary"
                onClick={onSave}
                disabled={!hasChanges}
                sx={{ mt: 3 }}
            >
                {localisation?.["save_button"]?.element_lang || "Save"}
            </Button>

            {/* SSL Certificates Section */}
            {!isCloud && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        SSL Certificates
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Upload SSL Certificates
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Upload private.key, cert.crt, and bundle.crt files for HTTPS
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                startIcon={<UploadFileIcon />}
                                onClick={() => setCertUploadModalOpen(true)}
                            >
                                Upload Certificates
                            </Button>
                        </Box>
                    </Paper>

                    <CertificatesUploadModal
                        visible={certUploadModalOpen}
                        toggle={() => setCertUploadModalOpen(false)}
                    />
                </>
            )}
        </Box>
    );
}

export default SystemConfigSection;
